import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  OnDragEndResponder,
} from 'react-beautiful-dnd';
import { twMerge } from 'tailwind-merge';

import CompetencyCard from 'components/CompetencyCard/CompetencyCard';
import StrictModeDroppable from 'components/StrictModeDroppable/StrictModeDroppable';
import { ICompetency } from 'hooks/useGetAssessmentSteps';
import useNotification from 'hooks/useNotification';
import useUpdateCompetencyOrder from 'hooks/useUpdateCompetencyOrder';
import { t } from 'locales/i18n';

interface DraggableCompetenciesProps {
  competencies: ICompetency[];
  interviewUid: string;
}

const DraggableCompetencies = ({
  competencies,
  interviewUid,
}: DraggableCompetenciesProps) => {
  const [items, setItems] = useState(competencies);
  const { updateCompetencyOrder } = useUpdateCompetencyOrder(interviewUid);
  const { showErrorNotification } = useNotification();
  const queryClient = useQueryClient();

  const reorder = (
    list: ICompetency[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd: OnDragEndResponder = async (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    setItems(reorderedItems);

    await updateCompetencyOrder(
      {
        competencies: reorderedItems,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['getAssessmentStepDetails', interviewUid],
          });
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );
  };

  const listClassName = (isDraggingOver: boolean) =>
    twMerge(
      'rounded',
      isDraggingOver ? 'bg-kira-slate-100' : 'bg-kira-slate-0'
    );

  const itemClassName = (isDragging: boolean) =>
    twMerge(
      isDragging ? 'drop-shadow-lg' : '',
      isDragging ? 'cursor-grabbing' : '!cursor-pointer',
      isDragging && '!pointer-events-auto'
    );

  useEffect(() => {
    setItems(competencies);
  }, [competencies]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId="competenciesDroppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={listClassName(snapshot.isDraggingOver)}
          >
            {items.map((competency, index) => (
              <Draggable
                key={competency.uid}
                draggableId={competency.uid}
                index={index}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={itemClassName(snapshot.isDragging)}
                    data-testid="competencies"
                  >
                    <CompetencyCard
                      key={competency.uid}
                      competency={competency}
                      interviewUid={interviewUid}
                      isDragged={snapshot.isDragging}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

export default DraggableCompetencies;
